import { Component, Input } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { SelectModalData } from '@app/interfaces';
import { MessagesService } from '@app/services/messages.service';
import { TranslateService } from '@ngx-translate/core';
import { AmKeyboardService } from '../am-keyboard/am-keyboard.service';
/**
  Componente personalizado para seleccionar opciones desde un modal.
  Este componente implementa `ControlValueAccessor` para integrarse con formularios reactivos de Angular.
  Permite abrir un modal con una lista de opciones y seleccionar un valor, que se refleja en el control del formulario.
**/
@Component({
    selector: 'am-select-form',
    templateUrl: './am-select-form.component.html',
    styleUrls: ['./am-select-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: AmSelectFormComponent,
            multi: true,
        },
    ],
    standalone: false
})

export class AmSelectFormComponent implements ControlValueAccessor {

/**
    Nombre del control del formulario.
    @type {string}
  **/
    @Input() formControlName: string;

    /**
      Mensaje de error cuando el patrón de validación no se cumple.
      @type {string}
    **/
    @Input() errorPattern = '';
  
    /**
      Mensaje de error cuando el campo es requerido y no se ha completado.
      @type {string}
    **/
    @Input() errorRequired = '';
  
    /**
      Indica si el input debe ocupar el ancho completo disponible.
      @type {boolean}
    **/
    @Input() inputFullSize = false;
  
    /**
      Indica si se debe mostrar un ícono para borrar el valor seleccionado.
      @type {boolean}
    **/
    @Input() iconDelete = true;
  
    /**
      Etiqueta que se muestra encima del input.
      @type {string}
    **/
    @Input() label = '';
  
    /**
      Texto de marcador de posición (placeholder) para el input.
      @type {string}
    **/
    @Input() placeholder = '';
  
    /**
      Control del formulario asociado al input.
      @type {any}
    **/
    @Input() textControl: any;
  
    /**
      Opciones que se mostrarán en el modal de selección.
      @type {SelectModalData}
    **/
    @Input() optionsSelect: SelectModalData;
  
    /**
      Valor seleccionado en el control.
      @type {any}
    **/
    @Input() value: any;
  
    /**
      Clase CSS personalizada para el componente.
      @type {string}
    **/
    @Input() className = '';
  
    /**
      Indica si el componente se usa para seleccionar la residencia según TIMATIC.
      @type {boolean}
    **/
    @Input() typeTimaticResidence = false;
  
    /**
      Indica si el input es de solo lectura.
      @type {boolean}
    **/
    @Input() readOnly: boolean = false;

  constructor(
    private _messageServ: MessagesService,
    private _localeServ: TranslateService,
    private _keyboardService: AmKeyboardService,
  ) {}

    /**
    Escribe el valor en el control del formulario.
    @param {any} value - Valor a escribir en el control.
    @return void
  **/
  writeValue(value: any): void {
    this.value = value;
  }
    /**
    Registra una función para notificar cambios en el valor del control.
    @param {any} fn - Función de notificación de cambios.
    @return void
  **/
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
   /**
    Registra una función para notificar cuando el control ha sido tocado.
    @param {any} fn - Función de notificación de "tocado".
    @return void
  **/
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
    /**
    Establece el estado de deshabilitado del control.
    @param {boolean} isDisabled - Indica si el control debe estar deshabilitado.
    @return void
  **/
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.textControl.disable();
    } else {
      this.textControl.enable();
    }
  }
  //#endregion ControlValueAccessor

  onChange: any = () => {};
  onTouched: any = () => {};

  /**
    Abre un modal con una lista de opciones para seleccionar.
    Si el control es de solo lectura, no se abre el modal.
    @return void
  **/
  openModalList(){
    if(this.readOnly) return;
    this._messageServ.showListSelect(this.optionsSelect)
      .then(res => {
        const currentLang = this._localeServ.currentLang.replace("-", "_");
        this.value = res[currentLang];
        this.textControl.setValue(this.value);
        this._keyboardService.hide();
      })
      .catch(() => {
      })
      .finally(() => {
        this._keyboardService.hide();
      })
  }
}
